import turfAlong from '@turf/along';
import { lineString } from '@turf/helpers';
import { reverseCoord } from '@alltrails/maps/utils/legacyGeoJSONConversions';
import { COLOR_TEXT_PRIMARY } from '@alltrails/shared/denali/tokens';
import { MapStatsUtil } from '../map_stats_util';
import * as d3 from 'd3';

const MOVING_AVERAGE_RELATIVE_SIZE = 1.0 / 1000.0;
const FLAT_GRADE_THRESHOLD = 3;
const GRADE_DIRECTION = {
  UP: 'UP',
  DOWN: 'DOWN',
  FLAT: 'FLAT'
};
const GRADE_DIFFICULTY = {
  HARD: 'HARD',
  MODERATE: 'MODERATE',
  EASY: 'EASY'
};

const defaultElevConfig = {
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  lineColor: '#FF0000',
  hoverLineColor: '#219AD8',
  areaColor: '#595952',
  tickColor: '#CCCCCC',
  textColor: 'rgba(55, 55, 50, 0.85)',
  axisTextColor: COLOR_TEXT_PRIMARY,
  endLineColor: 'rgba(55, 55, 50, 0.4)'
};

const averageGrade = (pointByPoint, atIndex, displayMetric) => {
  // Least-squares fit to points within 100m of selected point
  // Find at least one point ahead and behind, even if it's more than 100m
  const distanceSpan = 100.0 / (displayMetric ? 1000.0 : 1609.34);
  const centerDistance = pointByPoint[atIndex][0];
  let xSum = 0;
  let ySum = 0;
  let xySum = 0;
  let xxSum = 0;
  let count = 0;
  // Look backwards from selected point for 100m, then look forwards from it
  let movingBackwards = true;
  let i = atIndex;
  for (;;) {
    if (i == pointByPoint.length) {
      break;
    }
    const distanceFromCenter = pointByPoint[i][0] - centerDistance;
    if (movingBackwards) {
      if (-distanceFromCenter > distanceSpan && i != atIndex - 1) {
        movingBackwards = false;
        i = atIndex + 1;
        continue;
      }
    } else if (distanceFromCenter > distanceSpan && i != atIndex + 1) {
      break;
    }
    const x = pointByPoint[i][0] * (displayMetric ? 1000 : 5280);
    xSum += x;
    ySum += pointByPoint[i][1];
    xxSum += x * x;
    xySum += x * pointByPoint[i][1];
    count += 1;
    if (movingBackwards) {
      i -= 1;
      if (i == -1) {
        movingBackwards = false;
        i = atIndex + 1;
      }
    } else {
      i += 1; // This is checked against bounds on next iteration
    }
  }
  if (count > 1 && count * xxSum - xSum * xSum != 0.0) {
    const grade = (count * xySum - xSum * ySum) / (count * xxSum - xSum * xSum);
    return 100.0 * grade;
  }
  return 0;
};

/**
 * buildElevationGraphPointsData is budiling elevation graph points
 * to calculate difficulty segments.
 * This function is also calculating the elevation grade percent at all points.
 *
 * @param {Object[]} data - elevation points flatten data
 * @param {string} displayMetric
 * @returns array
 */
const buildElevationGraphPointsData = (data, displayMetric) => {
  const elevationGraphPoints = [];
  for (let i = 0; i < data.length; i++) {
    const x = data[i][0] * (displayMetric ? 1000 : 5280);
    const y = data[i][1];
    let gradePct = 0;

    if (i > 0) {
      // distance between points
      gradePct = ((data[i][1] - data[i - 1][1]) / (data[i][0] - data[i - 1][0])) * (displayMetric ? 1000 : 5280) * 100;
    }
    elevationGraphPoints.push({ x, y, gradePct });
  }

  return elevationGraphPoints;
};

/**
 * getGradeDirection is getting current graph direction
 * depends on grade treshold
 *
 * @param {number} grade
 * @returns string
 */
const getGradeDirection = grade => {
  if (grade > FLAT_GRADE_THRESHOLD) {
    return GRADE_DIRECTION.UP;
  }
  if (grade < -FLAT_GRADE_THRESHOLD) {
    return GRADE_DIRECTION.DOWN;
  }
  return GRADE_DIRECTION.FLAT;
};

/**
 * getMovingAverage is getting moving average
 *
 * @param {Object[]} points - elevation points builded data from buildElevationGraphPointsData function
 * @param {number} index
 * @param {number} movingAverageSize
 * @returns number
 */
const getMovingAverage = (points, index, movingAverageSize) => {
  const movingAverageSizeOdd = movingAverageSize % 2 === 0 ? movingAverageSize + 1 : movingAverageSize;
  const movingAverageSizeHalf = movingAverageSizeOdd / 2;

  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < movingAverageSizeOdd; i++) {
    let currIndexOffset;
    if (i < movingAverageSizeHalf) {
      currIndexOffset = -1 * (i + 1);
    } else if (i === movingAverageSizeHalf) {
      currIndexOffset = 0;
    } else {
      currIndexOffset = movingAverageSizeOdd - i;
    }
    const pointIndex = index + currIndexOffset;
    const prevPointIndex = pointIndex - 1;

    if (pointIndex < 0 || pointIndex >= points.length) continue;
    if (prevPointIndex < 0 || pointIndex >= points.length) continue;

    const point = points[pointIndex];

    const prevPoint = points[prevPointIndex];
    const pointValue = point.gradePct;

    const xDeltaWeight = point.x - prevPoint.x; // Calculate length of this segment to weight it appropriately

    numerator += pointValue * xDeltaWeight;
    denominator += xDeltaWeight;
  }

  return denominator === 0 ? 0 : numerator / denominator;
};

const getGrade = (index, elevationPointsData, movingAverageSize) => {
  return getMovingAverage(elevationPointsData, index, movingAverageSize);
};

/**
 * getGradeDifficulty is getting difficult net grade of uphill sections for each point
 * moderate: > 6, hard: > 11
 *
 * @param {number} grade
 * @returns string
 */
const getGradeDifficulty = grade => {
  if (grade > 11) {
    return GRADE_DIFFICULTY.HARD;
  }
  if (grade > 6) {
    return GRADE_DIFFICULTY.MODERATE;
  }
  return GRADE_DIFFICULTY.EASY;
};

const getBetweenPoints = (x0, x1, points, displayMetric) => {
  const indexOfFirstPoint = points.findIndex(p => p.x === x0);
  const indexOfLastPoint = points.findIndex(p => p.x === x1);
  const data = points.slice(indexOfFirstPoint, indexOfLastPoint);
  return data.map(d => {
    return { x: d.x / (displayMetric ? 1000 : 5280), y: d.y };
  });
};

/**
 * calculateDifficultySegments is find continuous segments where
 * the moving average grade is positive
 *
 * @param {Object[]} elevationGraphPoints
 * @param {string} displayMetric
 * @returns Object[]
 */
const calculateDifficultySegments = (elevationGraphPoints, displayMetric) => {
  const elevationPointsData = buildElevationGraphPointsData(elevationGraphPoints, displayMetric);
  const segments = [];
  // no grade available
  if (elevationPointsData.length <= 1) {
    return segments;
  }

  const firstPoint = elevationPointsData[0];
  const secondPoint = elevationPointsData[1];

  // store the starting x,y for the first continuous section of the trail
  let currX0 = firstPoint.x;
  let currY0 = firstPoint.y;

  // store the initial grade direction from point 0 to point 1
  let currGradeState = getGradeDirection(secondPoint.gradePct);

  // get moving average size
  const xLast = elevationPointsData[elevationPointsData.length - 1].x;
  const movingAverageSize = Math.max(xLast * MOVING_AVERAGE_RELATIVE_SIZE, 1);

  for (let i = 1; i < elevationPointsData.length; i++) {
    const point = elevationPointsData[i];
    const grade = getGrade(i, elevationPointsData, parseInt(movingAverageSize));
    const newGradeState = getGradeDirection(grade);

    if (
      currGradeState !== newGradeState || // If grade direction has changed
      i === elevationPointsData.length - 1 // or this is the last point in the graph
    ) {
      const x1 = point.x;
      const y1 = point.y;
      const netGradePct = ((y1 - currY0) / (x1 - currX0)) * 100;
      const difficultyEstimate = getGradeDifficulty(netGradePct);

      const x0 = currX0 / (displayMetric ? 1000 : 5280);
      const returnX1 = x1 / (displayMetric ? 1000 : 5280);
      let betweenPoints = getBetweenPoints(currX0, x1, elevationPointsData, displayMetric);

      segments.push({ x0, x1: returnX1, y0: currY0, y1, difficultyEstimate, betweenPoints });

      currX0 = x1;
      currY0 = y1;
      currGradeState = newGradeState;
      betweenPoints = [];
    }
  }
  return segments;
};

const getSegmentsFill = difficultyEstimate => {
  if (difficultyEstimate === 'HARD') {
    return 'rgba(224, 32, 32, 0.24)';
  }
  if (difficultyEstimate === 'MODERATE') {
    return 'rgba(255, 204, 0, 0.24)';
  }
  return 'none';
};

// Let D3 handle itself, its good at graphs, react good at dom management
const createElevChart = (
  {
    displayMetric = false,
    dom,
    elevationConfig = {},
    handleMouseOver,
    height = 130,
    paddingWidth = 0,
    pointByPointSegmented = [],
    colorsSegmented = [],
    showStatsGroup = false,
    totalDistance = 0,
    width,
    languageRegionCode = 'en-US',
    isPrintMap
  },
  intl
) => {
  const emptyChart = (!pointByPointSegmented && !totalDistance) || pointByPointSegmented.length === 0;
  const pointByPoint = pointByPointSegmented.flat();

  const segmentGaps = [];
  pointByPointSegmented.forEach((segment, i) => {
    if (i > 0) {
      segmentGaps.push(segment[0]);
    }
  });

  width = displayMetric ? width + 20 || dom?.clientWidth + 20 : width || dom?.clientWidth;
  const { lineColor, hoverLineColor, tickColor, textColor, axisTextColor } = Object.assign(defaultElevConfig, elevationConfig, {});
  const distanceUnits = displayMetric ? 'km' : intl.formatMessage({ defaultMessage: 'mi' });
  const elevLabelPos = width;
  const elevUnits = displayMetric ? 'm' : intl.formatMessage({ defaultMessage: 'ft' });
  const axisColor = isPrintMap ? '#414141' : axisTextColor;
  const newHeight = isPrintMap ? height + 20 : height;

  d3.select(dom).html('');
  const chart = d3
    .select(dom)
    .append('svg:svg')
    .attr('width', width + paddingWidth)
    .attr('height', newHeight);

  let minElevation = null;
  let maxElevation = null;
  if (emptyChart) {
    chart
      .append('svg:text')
      .attr('x', width / 2 - 90)
      .attr('y', height / 2)
      .attr('font-size', '20px')
      .attr('fill', textColor)
      .text(intl.formatMessage({ defaultMessage: 'No elevation data' }));
    minElevation = 10;
    maxElevation = 1000;
    totalDistance = 1.0;
  }

  const axisGroup = chart.append('svg:g').attr('pointer-events', 'none');
  if (!minElevation) {
    minElevation = d3.min(pointByPoint, datum => datum[1]);
  }
  if (!maxElevation) {
    maxElevation = d3.max(pointByPoint, datum => datum[1]);
  }

  const elevationRange = maxElevation - minElevation;
  // if there is no variation in elevation, draw a default scale with 50ft difference on either side
  let elevationBuffer = Math.min(elevationRange * 0.05, elevationRange * 2);

  let topElevation;
  let botElevation;
  const elevationRangeLowerThreshold = displayMetric ? 30.84 : 100;
  if (elevationRange < elevationRangeLowerThreshold) {
    elevationBuffer = (elevationRangeLowerThreshold - elevationRange) / 2;
    topElevation = maxElevation + elevationBuffer;
    botElevation = minElevation - elevationBuffer;
  } else {
    topElevation = maxElevation + elevationBuffer + elevationBuffer / 4;
    botElevation = minElevation - elevationBuffer - elevationBuffer / 4;
  }
  const totalVisibleRange = topElevation - botElevation;
  // Given an input in the elevation array, make it fit within width of elevation graph

  let xAxisEndValue = totalDistance;
  if (totalDistance < 1) {
    xAxisEndValue = xAxisEndValue.toFixed(2);
  } else if (totalDistance < 50) {
    xAxisEndValue = xAxisEndValue.toFixed(1);
  } else {
    xAxisEndValue = Math.floor(xAxisEndValue);
  }

  const yScaleHeight = isPrintMap ? height - 5 : height - 25;

  const xScale = d3.scaleLinear().domain([0, xAxisEndValue]).range([0, width]);
  const yScale = d3.scaleLinear().domain([topElevation, botElevation]).rangeRound([20, yScaleHeight]);
  const grdFormat = d3.format(',.0f');

  const strokeColor = isPrintMap ? '#e6e6e6' : tickColor;
  // max
  axisGroup
    .append('svg:text')
    .attr('text-anchor', 'end')
    .attr('font-size', 11)
    .attr('x', elevLabelPos)
    .attr('y', yScale(maxElevation + elevationBuffer) + 10)
    .attr('fill', axisColor)
    .text(MapStatsUtil.formatData(topElevation, 0, elevUnits, languageRegionCode));
  axisGroup
    .append('svg:line')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', Math.round(yScale(maxElevation + elevationBuffer)))
    .attr('y2', yScale(maxElevation + elevationBuffer))
    .attr('stroke', strokeColor);
  // min
  axisGroup
    .append('svg:text')
    .attr('text-anchor', 'end')
    .attr('font-size', 11)
    .attr('x', elevLabelPos)
    .attr('y', yScale(minElevation - elevationBuffer) - 13)
    .attr('fill', axisColor)
    .text(MapStatsUtil.formatData(botElevation, 0, elevUnits, languageRegionCode));

  if (totalVisibleRange === 0) {
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(minElevation - elevationBuffer) + 90)
      .attr('y2', yScale(minElevation - elevationBuffer) + 90)
      .attr('stroke', strokeColor);

    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(botElevation) + 67.5)
      .attr('y2', yScale(botElevation) + 67.5)
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(botElevation) + 45)
      .attr('y2', yScale(botElevation) + 45)
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(botElevation) + 22.5)
      .attr('y2', yScale(botElevation) + 22.5)
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
  } else {
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(minElevation - elevationBuffer))
      .attr('y2', yScale(minElevation - elevationBuffer))
      .attr('stroke', strokeColor);

    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(totalVisibleRange * 0.25 + botElevation))
      .attr('y2', yScale(totalVisibleRange * 0.25 + botElevation))
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(totalVisibleRange * 0.5 + botElevation))
      .attr('y2', yScale(totalVisibleRange * 0.5 + botElevation))
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
    axisGroup
      .append('svg:line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', yScale(totalVisibleRange * 0.75 + botElevation))
      .attr('y2', yScale(totalVisibleRange * 0.75 + botElevation))
      .attr('stroke', strokeColor)
      .attr('fill', 'none')
      .style('stroke-dasharray', '3, 3');
  }

  // axis needs less ticks to be more readable under certain widths
  // need to be careful of JS floating point issues, maxing out at displaying 3 decimals places

  const xAxisTicks = totalDistance < 1 ? 5 : 4;
  const xAxisInterval = totalDistance / xAxisTicks;
  const xAxis = d3
    .axisBottom()
    .scale(xScale)
    .tickSize(4, 0)
    .tickFormat((d, i, n) => {
      let cleanedNumber = Number(`${Math.round(`${d}e3`)}e-3`);
      let precision = 0;
      if (cleanedNumber % 1 === 0 && totalDistance < 50) {
        // add decimal points to whole numbers to keep sig figs consistent
        precision = 1;
      } else if (totalDistance < 1) {
        precision = 2;
      }
      if (n[i].__data__ && n[i + 1] && n[i + 1].__data__) {
        // Check if the second to last point is too close to the last point.
        const current = n[i].__data__;
        const next = n[i + 1].__data__;
        if (next - current < xAxisInterval / 2) {
          return '';
        }
      }
      if (!n[i + 1]) {
        // Hack to force total distance as last marker
        cleanedNumber = xAxisEndValue;
      }

      return MapStatsUtil.formatData(cleanedNumber, precision, distanceUnits, languageRegionCode);
    });

  xAxis.tickValues(xScale.ticks(xAxisTicks).concat(xAxisEndValue));
  const customXAxis = g => {
    const s = g.selection ? g.selection() : g;
    g.call(xAxis);
    g.select('.domain').remove();
    s.selectAll('.tick text').attr('fill', axisTextColor).style('font-size', '11px');
    s.selectAll('.tick line').attr('x1', -4).attr('x2', -4);
  };
  const heightTranslate = isPrintMap ? height - 5 : height - 30;
  const bottomAxisTranslate = `translate(5,${heightTranslate})`;
  axisGroup
    .append('svg')
    .attr('text-anchor', 'middle')
    .attr('class', 'xAxisBottom')
    .append('g')
    .attr('transform', bottomAxisTranslate)
    .call(customXAxis);

  if (emptyChart) {
    return;
  }

  // draw dotted lines where gaps in the recording exist
  for (let i = 0; i < segmentGaps.length; i++) {
    axisGroup
      .append('svg:line')
      .attr('x1', xScale(segmentGaps[i][0]) + 1)
      .attr('x2', xScale(segmentGaps[i][0]) + 1)
      .attr('y1', yScale(minElevation - elevationBuffer))
      .attr('y2', yScale(maxElevation + elevationBuffer))
      .attr('stroke', tickColor)
      .attr('stroke-width', '2')
      .style('stroke-dasharray', '4, 3');
  }

  // Elevation line helper
  let chartLine;
  const curve = d3.curveBasis;
  if (totalVisibleRange === 0) {
    chartLine = d3
      .line()
      .x(d => xScale(d[0]))
      .y(d => yScale(d[1]) + 45)
      .curve(curve);
  } else {
    chartLine = d3
      .line()
      .x(d => xScale(d[0]))
      .y(d => yScale(d[1]))
      .curve(curve);
  }

  const pathGroup = chart.append('svg:g').attr('pointer-events', 'none');

  for (let i = 0; i < pointByPointSegmented.length; i++) {
    const line = isPrintMap ? '#414141' : colorsSegmented[i] || lineColor;
    pathGroup
      .append('svg:path')
      .datum(pointByPointSegmented[i])
      .attr('d', chartLine(pointByPointSegmented[i]))
      .attr('stroke-width', '2')
      .attr('stroke', line)
      .attr('fill', 'none');
  }

  if (isPrintMap) {
    const fillArea = chart.append('svg:g').attr('pointer-events', 'none');
    const segments = calculateDifficultySegments(pointByPoint, displayMetric);

    const area = d3
      .area()
      .x(d => xScale(d.x))
      .y0(height - 5)
      .y1(d => yScale(d.y))
      .curve(curve);

    for (let i = 0; i < segments.length; i++) {
      const distance = segments[i].x1 - segments[i].x0;
      if (segments[i].difficultyEstimate !== 'EASY' || distance < 0.01) {
        const fillData = [];
        fillData.push({ x: segments[i].x0, y: segments[i].y0 }, { x: segments[i].x1, y: segments[i].y1 });

        for (let j = 0; j < segments[i].betweenPoints.length; j++) {
          fillData.push({ x: segments[i].betweenPoints[j].x, y: segments[i].betweenPoints[j].y });
        }

        fillArea.append('svg:path').attr('d', area(fillData)).attr('fill', getSegmentsFill(segments[i].difficultyEstimate));
      }
    }
  }

  const lineGroup = chart.append('svg:g').attr('pointer-events', 'none');
  lineGroup
    .append('svg:line')
    .attr('id', 'hoverline')
    .attr('x1', 0)
    .attr('y1', 0)
    .attr('x2', 0)
    .attr('y2', 0)
    .attr('stroke', hoverLineColor)
    .attr('stroke-width', '2')
    .attr('visibility', 'hidden');

  let statsGroup;
  let rectWidth;
  if (showStatsGroup) {
    // Calculate rectWidth based on maximum characters in required strings
    const maxChars = Math.max(
      intl.formatMessage({ defaultMessage: 'Distance' }).length,
      intl.formatMessage({ defaultMessage: 'Elevation' }).length,
      intl.formatMessage({ defaultMessage: 'Grade' }).length
    );

    rectWidth = maxChars * 6 + 80;
    // Stats group for currently hovered-over point (should be drawn last)
    const rectHeight = isPrintMap ? 190 : 76;
    statsGroup = chart.append('svg:g').attr('visibility', 'hidden').attr('pointer-events', 'none').attr('font-size', '13px').attr('font-weight', 500);
    statsGroup
      .append('svg:rect')
      .attr('x', 0)
      .attr('y', yScale(maxElevation + elevationBuffer) + 8)
      .attr('fill', 'black')
      .attr('height', rectHeight)
      .attr('width', rectWidth)
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('id', 'statsBoxShadow')
      .attr('filter', 'url(#stats-filter)');
    statsGroup
      .append('svg:rect')
      .attr('x', 0)
      .attr('y', yScale(maxElevation + elevationBuffer) + 8)
      .attr('fill', 'white')
      .attr('height', rectHeight)
      .attr('width', rectWidth)
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('id', 'statsBox');
    statsGroup
      .append('svg:text')
      .attr('x', 0)
      .attr('y', yScale(maxElevation + elevationBuffer) + 32)
      .attr('fill', textColor)
      .attr('id', 'currDist')
      .text('');
    statsGroup
      .append('svg:text')
      .attr('x', 0)
      .attr('y', yScale(maxElevation + elevationBuffer) + 51)
      .attr('fill', textColor)
      .attr('id', 'currElev')
      .text('');
    statsGroup
      .append('svg:text')
      .attr('x', 0)
      .attr('y', yScale(maxElevation + elevationBuffer) + 70)
      .attr('fill', textColor)
      .attr('id', 'currGrd')
      .text('');

    // Stats group's dropshadow
    const statsGroupFilter = statsGroup.append('svg:defs').append('svg:filter').attr('id', 'stats-filter');
    statsGroupFilter
      .append('svg:feColorMatrix')
      .attr('result', 'colorOut')
      .attr('in', 'SourceGraphic')
      .attr('type', 'matrix')
      .attr('values', '0.2 0 0 0 0 0 0.2 0 0 0 0 0 0.2 0 0 0 0 0 1 0');
    statsGroupFilter.append('svg:feGaussianBlur').attr('result', 'blurOut').attr('in', 'colorOut').attr('stdDeviation', 2);
  }

  const touchOrMouseEnd = () => {
    if (handleMouseOver) {
      handleMouseOver(null);
    }
    lineGroup.select('#hoverline').attr('visibility', 'hidden');
    if (statsGroup) {
      statsGroup.attr('visibility', 'hidden');
    }
  };

  const touchOrMouseMove = xPos => {
    // Position is out-of-bounds of graph
    if (xPos < 0 || xPos > width) {
      touchOrMouseEnd();
      return;
    }

    // Make lineGroup visible and move to mouse location
    lineGroup
      .select('#hoverline')
      .attr('visibility', 'visible')
      .attr('x1', xPos)
      .attr('y1', yScale(minElevation - elevationBuffer))
      .attr('x2', xPos)
      .attr('y2', yScale(maxElevation + elevationBuffer) + (totalVisibleRange === 0 ? 90 : 0));

    const currDistance = xScale.invert(xPos);

    // Get closest pointByPoint index JUST AFTER xPos
    let closestIndex = -1;
    for (let i = 1; i < pointByPoint.length; i++) {
      if (pointByPoint[i][0] > currDistance) {
        closestIndex = i - 1;
        break;
      }
    }
    // Modify to be closest pointByPoint index BEFORE xPos
    if (closestIndex < 0) {
      closestIndex = pointByPoint.length - 1;
    }

    // If 'handleMouseOver' callback given, interpret closest point on the line using graph coordinate and return in callback
    if (handleMouseOver && closestIndex >= 0 && closestIndex < pointByPoint.length - 1) {
      const prevPoint = reverseCoord(pointByPoint[closestIndex][2]);
      const nextPoint = reverseCoord(pointByPoint[closestIndex + 1][2]);
      const coords = lineString([prevPoint, nextPoint]);

      const prevDist = pointByPoint[closestIndex][0];
      const distOver = currDistance - prevDist;

      const distanceKm = displayMetric ? distOver : distOver * 1.60394;
      const interpLngLat = turfAlong(coords, distanceKm).geometry.coordinates;
      handleMouseOver(interpLngLat);
    }

    // Make statsGroup visible and move to mouse location
    if (statsGroup) {
      statsGroup
        .select('#currDist')
        .text(
          `${intl.formatMessage({ defaultMessage: 'Distance' })}: ${MapStatsUtil.formatData(currDistance, 1, distanceUnits, languageRegionCode)}`
        );
      statsGroup
        .select('#currElev')
        .text(
          `${intl.formatMessage({ defaultMessage: 'Elevation' })}: ${MapStatsUtil.formatData(
            pointByPoint[closestIndex][1],
            0,
            elevUnits,
            languageRegionCode
          )}`
        );

      const currGrade = statsGroup.select('#currGrd');
      if (pointByPoint.length >= 3 && closestIndex >= 0 && closestIndex < pointByPoint.length - 1) {
        const grade = averageGrade(pointByPoint, closestIndex, displayMetric);
        currGrade.text(`${intl.formatMessage({ defaultMessage: 'Grade' })}: ${grdFormat(grade)}%`);
      } else {
        currGrade.text('');
      }

      const isMobileWidth = window.innerWidth <= 767;
      let leftOffset;
      let rightOffset;
      if (isMobileWidth) {
        leftOffset = rectWidth + 60;
        rightOffset = 40;
      } else {
        leftOffset = rectWidth + 20;
        rightOffset = 0;
      }

      const currX = xPos + 10;
      const rectOffset = currX < leftOffset ? rightOffset : -leftOffset;
      const textOffset = 10;
      statsGroup.selectAll('rect').attr('x', currX + rectOffset);
      statsGroup.selectAll('text').attr('x', currX + rectOffset + textOffset);
      statsGroup.attr('visibility', 'visible');
    }
  };

  const handleMouseEvent = event => {
    const xPos = d3.pointer(event, chart.node())[0];
    touchOrMouseMove(xPos);
  };

  const handleTouchEvent = event => {
    const xPos = d3.pointer(event.touches[0], chart.node())[0];
    touchOrMouseMove(xPos);
  };

  chart
    .on('mouseover', handleMouseEvent)
    .on('mousemove', handleMouseEvent)
    .on('mouseout', () => {
      touchOrMouseEnd();
    });

  // hover equivalent for touch devices
  chart
    .on('touchstart', handleTouchEvent)
    .on('touchmove', handleTouchEvent)
    .on('touchend', () => {
      touchOrMouseEnd();
    });
};

export { createElevChart };
